<script setup lang="ts">
import type { MediaSummary } from '../../shared/contracts'
import { Search, X } from '@lucide/vue'
import { computed, nextTick, onActivated, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MediaGrid from '@/components/media/MediaGrid.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import MediaSkeleton from '@/components/ui/MediaSkeleton.vue'
import { useHorizontalSwipe } from '@/composables/useHorizontalSwipe'
import { api } from '@/services/api'
import { useAppStore } from '@/stores/app'
import { useContentSourcesStore } from '@/stores/contentSources'
import { useLibraryStore } from '@/stores/library'
import { mergeMediaResults } from '@/utils/media'
import { MAX_ENABLED_SOURCES } from '../../shared/content-sources'

defineOptions({ name: 'SearchView' })

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const library = useLibraryStore()
const contentSources = useContentSourcesStore()
const query = ref(String(route.query.q || ''))
const source = ref(String(route.query.source || ''))
const items = ref<MediaSummary[]>([])
const loading = ref(false)
const searched = ref(false)
const error = ref('')
const sourceScroller = ref<HTMLElement>()
const resultCache = new Map<string, { items: MediaSummary[], updatedAt: number }>()
let timer: number | undefined
let searchRequest = 0
let lastSearchedAt = 0
let activatedOnce = false
const countLabel = computed(() => searched.value && !loading.value ? `${items.value.length} 个结果` : '')
const availableSources = computed(() => {
    const merged = new Map([...appStore.activeSources, ...contentSources.sources].map(item => [item.id, item]))
    return [...merged.values()].filter(item => item.searchable !== false).slice(0, MAX_ENABLED_SOURCES)
})
const searchTabs = computed(() => [
    { id: '', name: '全部来源' },
    ...availableSources.value,
])

function switchSourceBySwipe(direction: -1 | 1) {
    const currentIndex = Math.max(0, searchTabs.value.findIndex(item => item.id === source.value))
    const next = searchTabs.value[currentIndex + direction]
    if (next) { source.value = next.id }
}

const sourceSwipe = useHorizontalSwipe(switchSourceBySwipe)

function centerSelectedSource(behavior: ScrollBehavior = 'smooth') {
    const scroller = sourceScroller.value
    if (!scroller) { return }
    const selected = [...scroller.querySelectorAll<HTMLElement>('[data-source-id]')]
        .find(item => item.dataset.sourceId === source.value)
    if (!selected) { return }
    const left = selected.offsetLeft - (scroller.clientWidth - selected.offsetWidth) / 2
    scroller.scrollTo({ left: Math.max(0, left), behavior })
}

async function searchNow(background = false) {
    const requestId = ++searchRequest
    const value = query.value.trim()
    if (!value) {
        items.value = []
        searched.value = false
        return
    }
    error.value = ''
    await router.replace({ query: { q: value, ...(source.value ? { source: source.value } : {}) } })
    const cacheKey = `${value.toLocaleLowerCase()}:${source.value || 'all'}`
    const cached = resultCache.get(cacheKey)
    if (cached) {
        items.value = cached.items
        searched.value = true
        loading.value = false
        lastSearchedAt = cached.updatedAt
        if (!background && Date.now() - cached.updatedAt < 30_000) { return }
        background = true
    }
    if (!background) { loading.value = true }
    try {
        const selectedSources = source.value
            ? availableSources.value.filter(item => item.id === source.value)
            : availableSources.value
        const localIds = new Set(contentSources.sources.map(item => item.id))
        const localSources = selectedSources.flatMap(item => contentSources.findSource(item.id) ?? [])
        const remoteSources = selectedSources.filter(item => !localIds.has(item.id))
        const requests = []
        remoteSources.forEach(item => requests.push(api.search(value, item.id)))
        if (localSources.length) { requests.push(api.searchSources(value, localSources)) }
        const results = await Promise.allSettled(requests)
        if (requestId !== searchRequest) { return }
        items.value = mergeMediaResults(results.map(result => result.status === 'fulfilled' ? result.value.items : []))
        lastSearchedAt = Date.now()
        resultCache.delete(cacheKey)
        resultCache.set(cacheKey, { items: items.value, updatedAt: lastSearchedAt })
        while (resultCache.size > 20) {
            const oldest = resultCache.keys().next().value
            if (!oldest) { break }
            resultCache.delete(oldest)
        }
    }
    catch (caught) {
        if (requestId === searchRequest && (!background || !items.value.length)) { error.value = caught instanceof Error ? caught.message : '搜索失败' }
    }
    finally {
        if (requestId === searchRequest) {
            loading.value = false
            searched.value = true
        }
    }
}

function scheduleSearch() {
    window.clearTimeout(timer)
    if (!query.value.trim()) {
        clearSearch()
        return
    }
    timer = window.setTimeout(searchNow, 350)
}

function clearSearch() {
    window.clearTimeout(timer)
    searchRequest++
    query.value = ''
    items.value = []
    loading.value = false
    searched.value = false
    error.value = ''
    void router.replace({ query: {} })
}

watch(source, () => {
    nextTick(() => centerSelectedSource())
    if (query.value.trim()) { searchNow() }
})
watch(availableSources, () => nextTick(() => centerSelectedSource('auto')))
onMounted(async () => {
    await appStore.initialize()
    await contentSources.initialize()
    if (query.value.trim()) { searchNow() }
})
onActivated(() => {
    if (!activatedOnce) {
        activatedOnce = true
        return
    }
    if (searched.value && query.value.trim() && Date.now() - lastSearchedAt > 60_000) { void searchNow(true) }
})
onBeforeUnmount(() => {
    searchRequest++
    window.clearTimeout(timer)
})
</script>

<template>
    <div class="page-shell">
        <header class="grid gap-8 border-b border-[var(--c-border)] pb-9 md:grid-cols-[minmax(240px,.7fr)_minmax(420px,1.3fr)] md:items-end md:pb-12">
            <div>
                <p class="mb-3 text-[10px] text-[var(--accent)] font-700 uppercase">
                    找到想看的
                </p>
                <h1 class="page-title leading-none">
                    搜索
                </h1>
                <p class="mt-4 max-w-sm text-sm text-[var(--c-text-muted)] leading-6">
                    输入片名、演员或关键词，搜索所有已启用的来源。
                </p>
            </div>
            <form class="relative max-w-2xl" @submit.prevent="searchNow()">
                <Search class="pointer-events-none absolute left-0 top-1/2 size-[18px] text-[var(--c-text-muted)] -translate-y-1/2" />
                <input
                    v-model="query"
                    autofocus
                    type="search"
                    autocomplete="off"
                    placeholder="输入片名、演员或关键词"
                    class="h-12 w-full border-b border-[var(--c-border-strong)] bg-transparent pl-8 pr-10 text-base text-[var(--c-text)] outline-none transition sm:h-13 focus:border-[var(--accent)] sm:text-lg placeholder:text-[var(--c-text-faint)]"
                    @input="scheduleSearch"
                >
                <button v-if="query" type="button" class="absolute right-0 top-1/2 grid size-9 place-items-center rounded-full text-[var(--c-text-muted)] -translate-y-1/2 hover:bg-[var(--c-fill-hover)] hover:text-[var(--c-text)] focus-ring" aria-label="清空搜索" @click="clearSearch">
                    <X class="size-4" />
                </button>
            </form>
        </header>

        <div ref="sourceScroller" class="scrollbar-none flex items-center gap-7 overflow-x-auto border-b border-[var(--c-border)]">
            <button v-for="item in searchTabs" :key="item.id || 'all'" :data-source-id="item.id" class="relative min-h-14 shrink-0 rounded-sm text-[15px] font-600 transition lg:text-base focus-ring" :class="source === item.id ? 'text-[var(--c-text)] after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-[var(--accent)]' : 'text-[var(--c-text-muted)] hover:text-[var(--c-text)]'" @click="source = item.id">
                {{ item.name }}
            </button>
        </div>

        <section class="search-swipe-pane mt-10" @touchstart="sourceSwipe.onTouchStart" @touchend="sourceSwipe.onTouchEnd" @touchcancel="sourceSwipe.onTouchCancel">
            <div v-if="searched || loading" class="mb-7 flex items-end justify-between">
                <h2 class="section-title">
                    检索结果
                </h2>
                <span class="text-sm text-[var(--c-text-muted)] uppercase">{{ countLabel }}</span>
            </div>
            <MediaSkeleton v-if="loading" />
            <EmptyState v-else-if="error" title="搜索遇到问题" :description="error" />
            <MediaGrid v-else-if="items.length" :items="items" :records="library.history" :from="route.fullPath" />
            <EmptyState v-else-if="searched" title="没有匹配结果" description="尝试更短的关键词，或切换其他内容来源。" />
            <EmptyState v-else title="想看什么？" description="输入片名、演员或关键词开始搜索。" />
        </section>
    </div>
</template>

<style scoped>
.search-swipe-pane {
    min-height: max(18rem, 55dvh);
    touch-action: pan-y;
    overscroll-behavior-x: none;
    user-select: none;
}
</style>
